import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { useEffect } from "react";
import { updateBankAccount } from "../../../api/bank_account";

type Props = {
  show: boolean;
  account: any;
  onClose: () => void;
  onSaved?: () => void;
};

const EditAccountModal = ({ account, show, onClose, onSaved }: Props) => {
  const createSchema = z.object({
    account_title: z.string().nonempty("Le titre du compte est requis"),
    bank_name: z.string().nonempty("Le nom de la banque est requis"),
    type: z.string().nonempty("Veuillez sélectionner le type de compte"),
    account_number: z.string().nonempty("Le numéro de compte est requis"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      account_title: account?.account_title || "",
      bank_name: account?.bank_name || "",
      type: account?.type || "",
      account_number: account?.account_number || "",
    },
  });

  useEffect(() => {
    if (!show) return;
    setValue("account_title", account?.account_title || "");
    setValue("account_number", account?.account_number || "");
    setValue("bank_name", account?.bank_name || "");
    setValue("type", account?.type || "");
  }, [account, show]);

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const finalData = {
        ...data,
      };
      //Envoi des datas pour création vers le backend
      await updateBankAccount(account?.id, finalData);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Niveau d'étude ajouté !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Modifier Compte Bancaire</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white">
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Titre du compte <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("account_title")}
                    type="text"
                    className={
                      errors.bank_name
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: Compte Courant N°1"
                  />
                  {errors.account_title && (
                    <div className="invalid-feedback">
                      {errors.account_title.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Type de compte</label>
                  <select
                    {...register("type")}
                    defaultValue={account?.type || ""}
                    aria-label="Default select example"
                    className={
                      errors.type ? "form-select is-invalid" : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    <option value={"Courant"}>Courant</option>
                    <option value={"Epargne"}>Épargne</option>
                  </select>
                  {errors.type && (
                    <div className="invalid-feedback">
                      {errors.type.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Nom de la Banque
                  </label>
                  <input
                    {...register("bank_name")}
                    type="text"
                    className={
                      errors.bank_name
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: BMS.SA ou ECOBANK"
                  />
                  {errors.bank_name && (
                    <div className="invalid-feedback">
                      {errors.bank_name.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    N° de Compte
                  </label>
                  <input
                    {...register("account_number")}
                    type="text"
                    className={
                      errors.account_number
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: 123 4567 890"
                  />
                  {errors.account_number && (
                    <div className="invalid-feedback">
                      {errors.account_number.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditAccountModal,
  [buildPermission(Resource.PAYMENT_SETTING, Action.UPDATE)],
  "masquer",
);
